import { useEffect, type ReactNode } from "react";
import { MarketingFooter, MarketingHeader } from "../public/MarketingChrome";

export type BlogFaq = {
  question: string;
  answer: string;
};

export type BlogAuthorBioPart = {
  text: string;
  href?: string;
};

export type BlogPost = {
  path: string;
  title: string;
  description: string;
  publishedAt: string;
  updatedAt?: string;
  author: {
    name: string;
    role: string;
    image: string;
    website: string;
    linkedin?: string;
    twitter?: string;
    bio: BlogAuthorBioPart[];
  };
  image: string;
  imageAlt: string;
  imageWidth?: number;
  imageHeight?: number;
  readingTime: string;
  featured?: boolean;
  tags: string[];
  faqs: BlogFaq[];
};

const SITE_URL = "https://meetdoorway.com";

export function BlogPostPage({ post, children }: { post: BlogPost; children: ReactNode }) {
  useBlogMetadata(post);

  return (
    <div className="blog-shell oasis-marketing-shell">
      <MarketingHeader current="blog" />

      <main>
        <article className="blog-post">
          <header className="blog-hero">
            <a className="blog-kicker" href="/blog">Doorway blog</a>
            <h1>{post.title}</h1>
            <p className="blog-dek">{post.description}</p>
            <div className="blog-byline">
              <a className="author-headshot" href={post.author.website} rel="author me">
                <img
                  src={post.author.image}
                  alt={`${post.author.name} headshot`}
                  width="56"
                  height="56"
                />
              </a>
              <div className="author-details">
                <a className="author-name" href={post.author.website} rel="author me">
                  {post.author.name}
                </a>
                <span>{post.author.role}</span>
                <span className="author-socials" aria-label={`${post.author.name}'s social profiles`}>
                  {post.author.linkedin ? (
                    <a
                      className="author-social-link"
                      href={post.author.linkedin}
                      target="_blank"
                      rel="me noopener noreferrer"
                      aria-label={`${post.author.name} on LinkedIn`}
                      title="LinkedIn"
                    >
                      <LinkedInIcon />
                    </a>
                  ) : null}
                  {post.author.twitter ? (
                    <a
                      className="author-social-link"
                      href={post.author.twitter}
                      target="_blank"
                      rel="me noopener noreferrer"
                      aria-label={`${post.author.name} on Twitter`}
                      title="Twitter"
                    >
                      <TwitterIcon />
                    </a>
                  ) : null}
                </span>
              </div>
              <span className="byline-divider" aria-hidden="true" />
              <div>
                <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
                <span>{post.readingTime}</span>
              </div>
            </div>
          </header>

          <figure className="blog-header-image">
            <img
              src={post.image}
              alt={post.imageAlt}
              width={post.imageWidth ?? 1440}
              height={post.imageHeight ?? 810}
            />
          </figure>

          <div className="blog-body">
            {children}
          </div>

          <BlogFaqSection faqs={post.faqs} />

          <footer className="blog-post-footer">
            <p>Give the apps your team makes one dependable place to live.</p>
            <a className="primary-button" href="/manage">
              Start with Doorway
            </a>
          </footer>

          <BlogAuthorBio author={post.author} />
        </article>
      </main>

      <MarketingFooter />
    </div>
  );
}

function BlogAuthorBio({ author }: { author: BlogPost["author"] }) {
  return (
    <section className="author-bio" aria-labelledby="author-bio-heading">
      <a className="author-bio-headshot" href={author.website} rel="author me">
        <img src={author.image} alt={`${author.name} headshot`} width="92" height="92" />
      </a>
      <div className="author-bio-copy">
        <p className="blog-section-label">About the author</p>
        <h2 id="author-bio-heading">
          <a href={author.website} rel="author me">{author.name}</a>
        </h2>
        <p>
          {author.bio.map((part, index) => part.href ? (
            <a key={`${part.text}-${index}`} href={part.href} target="_blank" rel="noopener noreferrer">
              {part.text}
            </a>
          ) : (
            <span key={`${part.text}-${index}`}>{part.text}</span>
          ))}
        </p>
        <div className="author-bio-socials" aria-label={`${author.name}'s social profiles`}>
          {author.linkedin ? (
            <a href={author.linkedin} target="_blank" rel="me noopener noreferrer">
              <LinkedInIcon />
              LinkedIn
            </a>
          ) : null}
          {author.twitter ? (
            <a href={author.twitter} target="_blank" rel="me noopener noreferrer">
              <TwitterIcon />
              Twitter
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function BlogFaqSection({ faqs }: { faqs: BlogFaq[] }) {
  return (
    <section className="blog-faq" aria-labelledby="faq-heading">
      <p className="blog-section-label">A few practical questions</p>
      <h2 id="faq-heading">Frequently asked questions</h2>
      <div className="faq-list">
        {faqs.map((faq) => (
          <details key={faq.question}>
            <summary>{faq.question}</summary>
            <p>{faq.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function LinkedInIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.47-.9 1.64-1.85 3.37-1.85 3.61 0 4.27 2.37 4.27 5.46v6.28ZM5.32 7.43a2.07 2.07 0 1 1 0-4.13 2.07 2.07 0 0 1 0 4.13ZM7.1 20.45H3.54V9H7.1v11.45Z" />
    </svg>
  );
}

function TwitterIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M18.9 2.25h3.68l-8.04 9.19L24 21.75h-7.4l-5.8-7.58-6.63 7.58H.48l8.6-9.83L0 2.25h7.59l5.23 6.92 6.08-6.92Zm-1.3 17.68h2.04L6.48 3.98H4.3L17.6 19.93Z" />
    </svg>
  );
}

function useBlogMetadata(post: BlogPost) {
  useEffect(() => {
    const canonicalUrl = new URL(post.path, SITE_URL).toString();
    const imageUrl = new URL(post.image, SITE_URL).toString();
    const authorImageUrl = new URL(post.author.image, SITE_URL).toString();
    const authorBio = post.author.bio.map((part) => part.text).join("");

    document.title = `${post.title} | Doorway`;
    setMeta("name", "description", post.description);
    setMeta("name", "author", post.author.name);
    setMeta("name", "robots", "index, follow, max-image-preview:large");
    setMeta("property", "og:type", "article");
    setMeta("property", "og:site_name", "Doorway");
    setMeta("property", "og:title", post.title);
    setMeta("property", "og:description", post.description);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", imageUrl);
    setMeta("property", "og:image:alt", post.imageAlt);
    setMeta("property", "article:published_time", post.publishedAt);
    setMeta("property", "article:author", post.author.website);
    if (post.updatedAt) setMeta("property", "article:modified_time", post.updatedAt);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", post.title);
    setMeta("name", "twitter:description", post.description);
    setMeta("name", "twitter:image", imageUrl);

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.append(canonical);
    }
    canonical.href = canonicalUrl;

    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.description,
        datePublished: post.publishedAt,
        dateModified: post.updatedAt ?? post.publishedAt,
        mainEntityOfPage: canonicalUrl,
        image: imageUrl,
        keywords: post.tags.join(", "),
        author: {
          "@type": "Person",
          name: post.author.name,
          url: post.author.website,
          image: authorImageUrl,
          description: authorBio,
          sameAs: [post.author.linkedin, post.author.twitter].filter(Boolean),
        },
        publisher: { "@type": "Organization", name: "Doorway", url: SITE_URL },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      },
    ];

    schemas.forEach((schema, index) => {
      let script = document.querySelector<HTMLScriptElement>(`script[data-blog-schema="${index}"]`);
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.blogSchema = String(index);
        document.head.append(script);
      }
      script.text = JSON.stringify(schema);
    });

    return () => {
      document.querySelectorAll("script[data-blog-schema]").forEach((script) => script.remove());
    };
  }, [post]);
}

function setMeta(attribute: "name" | "property", key: string, content: string) {
  let element = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.append(element);
  }
  element.content = content;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(value));
}
